import { useEffect, useRef, useState, type FormEvent } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { createDispatchSession, resolveDispatchProperty } from '~/server/dispatch'
import { jobSearchSchema } from '~/lib/job-search'
import { PageHeader } from '~/components/PageHeader'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'

export const Route = createFileRoute('/')({
  validateSearch: jobSearchSchema,
  component: DispatchEntry,
})

const DEFAULT_RADIUS = 10
const DEFAULT_INTERVAL = 60

function DispatchEntry() {
  const navigate = useNavigate()
  const { job: linkedJob } = Route.useSearch()
  const [jobInput, setJobInput] = useState(linkedJob ?? '')
  const [creating, setCreating] = useState(false)
  const autoStarted = useRef(false)

  async function startSession(jobNumber: string, { replace = false } = {}) {
    setCreating(true)
    try {
      // Resolve the job to its property first — an unresolvable job is an
      // error (no session is created), and the resolved asset is stored on
      // the session for display + analytics.
      const property = await resolveDispatchProperty({ data: { jobNumber } })
      const session = await createDispatchSession({
        data: {
          jobNumber,
          assetNumber: property.assetNumber,
          radius: DEFAULT_RADIUS,
          interval: DEFAULT_INTERVAL,
          assetLat: property.lat,
          assetLon: property.lon,
        },
      })
      await navigate({
        to: '/dispatch/$sessionId',
        params: { sessionId: session.id },
        search: { radius: DEFAULT_RADIUS, interval: DEFAULT_INTERVAL },
        replace,
      })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start a dispatch session')
      setCreating(false)
    }
  }

  // `/?job=…` starts the session straight away; replacing the history entry
  // stops Back from re-opening the link and creating another one.
  useEffect(() => {
    if (!linkedJob || autoStarted.current) return
    autoStarted.current = true
    void startSession(linkedJob, { replace: true })
  }, [linkedJob]) // eslint-disable-line react-hooks/exhaustive-deps

  function onSearch(e: FormEvent) {
    e.preventDefault()
    const jobNumber = jobInput.trim()
    if (!jobNumber) {
      toast.error('Enter a valid job number')
      return
    }
    void startSession(jobNumber)
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="First Response"
        description="Enter a job number to find the nearest available officers and live jobs around its property."
      />
      <form onSubmit={onSearch} className="flex max-w-md gap-2">
        <Input
          type="text"
          placeholder="Enter job number… (e.g. J21303142)"
          value={jobInput}
          onChange={(e) => setJobInput(e.target.value)}
        />
        <Button type="submit" disabled={creating}>
          {creating ? 'Starting…' : 'Search'}
        </Button>
      </form>
    </div>
  )
}
